import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Save,
  Send,
  X as CancelIcon,
  Upload,
  Trash2,
  Loader2,
  Lock,
} from 'lucide-react';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Textarea } from 'components/ui/textarea';
import { Alert, AlertDescription } from 'components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import LoadingState from 'components/shared/LoadingState';
import TipTapEditor from './TipTapEditor';
import {
  getMyBlogPost,
  createBlogPost,
  updateBlogPost,
  getBlogCategories,
  uploadBlogImage,
} from '../../services/api';

// Deep comparison for form data
const isEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b);

// FPL seasons run Aug–May: from July onwards the "current" season is the one
// starting this calendar year, e.g. July 2026 → "2026-27".
const getCurrentSeason = () => {
  const now = new Date();
  const startYear = now.getMonth() >= 6 ? now.getFullYear() : now.getFullYear() - 1;
  return `${startYear}-${String(startYear + 1).slice(2)}`;
};

const EMPTY_FORM = {
  title: '',
  excerpt: '',
  content: { type: 'doc', content: [] },
  featured_image_url: '',
  status: 'draft',
  category_id: '',
  season: '',
  gameweek: '',
  meta_title: '',
  meta_description: '',
};

export default function BlogPostEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id;

  const fileInputRef = useRef(null);

  // Form state
  const [formData, setFormData] = useState(EMPTY_FORM);

  // Original data for comparison
  const [originalData, setOriginalData] = useState(null);

  // UI state
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingAction, setSavingAction] = useState(null); // 'draft' | 'pending_review' | 'save' | null
  const [uploading, setUploading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const saving = Boolean(savingAction);

  // Published/archived posts are managed by DraftFC — render read-only
  const readOnly =
    formData.status === 'published' || formData.status === 'archived';

  // Check if form has been modified
  const isDirty = useMemo(() => {
    if (!originalData) return false;
    return !isEqual(formData, originalData);
  }, [formData, originalData]);

  // Load data on mount
  const fetchData = useCallback(async () => {
    setLoading(true);
    setLoadError(null);

    try {
      const categoriesRes = await getBlogCategories();
      setCategories(categoriesRes.categories || []);

      if (!isNew) {
        // Fetch existing post
        const postRes = await getMyBlogPost(id);
        const post = postRes.post;

        const data = {
          title: post.title || '',
          excerpt: post.excerpt || '',
          content: post.content || { type: 'doc', content: [] },
          featured_image_url: post.featured_image_url || '',
          status: post.status || 'draft',
          category_id: post.category_id || '',
          season: post.season || '',
          gameweek: post.gameweek || '',
          meta_title: post.meta_title || '',
          meta_description: post.meta_description || '',
        };

        setFormData(data);
        setOriginalData(data);
      } else {
        // Set defaults for new post
        const data = {
          ...EMPTY_FORM,
          season: getCurrentSeason(),
        };

        setFormData(data);
        setOriginalData(data);
      }
    } catch (err) {
      setLoadError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [id, isNew]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const setField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleContentChange = useCallback((content) => {
    setFormData((prev) => ({ ...prev, content }));
  }, []);

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const result = await uploadBlogImage(file);
      setFormData((prev) => ({ ...prev, featured_image_url: result.url }));
      toast.success('Image uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Failed to upload image');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemoveImage = () => {
    setFormData((prev) => ({ ...prev, featured_image_url: '' }));
  };

  // Generate season options around the current season
  const seasonOptions = useMemo(() => {
    const current = getCurrentSeason(); // e.g. "2026-27"
    const startYear = parseInt(current.split('-')[0], 10);
    let options = [
      `${startYear - 1}-${String(startYear).slice(2)}`,
      current,
      `${startYear + 1}-${String(startYear + 2).slice(2)}`,
    ];
    // Keep an existing (older) post season selectable even if it falls outside
    // the generated prev/current/next window.
    if (formData.season && !options.includes(formData.season)) {
      options = [formData.season, ...options];
    }
    return options;
  }, [formData.season]);

  // Save with a target status:
  //   'draft'          → Save draft
  //   'pending_review' → Submit for review (DraftFC reviews + publishes)
  //   undefined        → keep the post's current status (Cmd+S)
  const handleSave = useCallback(
    async (targetStatus) => {
      if (saving || readOnly) return;
      if (!formData.title.trim()) {
        toast.error('Title is required');
        return;
      }

      const status = targetStatus || formData.status || 'draft';
      setSavingAction(targetStatus || 'save');

      try {
        const payload = {
          ...formData,
          status,
          category_id: formData.category_id || null,
          gameweek: formData.gameweek ? parseInt(formData.gameweek, 10) : null,
        };

        if (isNew) {
          const result = await createBlogPost(payload);
          // Navigate to edit the newly created post
          navigate(`/blog/edit/${result.post.id}`, { replace: true });
        } else {
          await updateBlogPost({ id, ...payload });
        }

        if (status === 'pending_review') {
          toast.success(
            'Submitted for review — DraftFC will review your post and publish it.'
          );
        } else {
          toast.success(isNew ? 'Draft created' : 'Draft saved');
        }

        // Update original data to match saved state
        const savedData = { ...formData, status };
        setFormData(savedData);
        setOriginalData(savedData);
      } catch (err) {
        toast.error(err.message || 'Failed to save post');
      } finally {
        setSavingAction(null);
      }
    },
    [saving, readOnly, formData, isNew, id, navigate]
  );

  // Ctrl/Cmd+S saves — the single most used power-user shortcut in an editor
  const handleSaveRef = useRef(handleSave);
  handleSaveRef.current = handleSave;
  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        handleSaveRef.current();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const handleBack = () => {
    if (isDirty) {
      setConfirmDialogOpen(true);
    } else {
      navigate('/blog');
    }
  };

  const handleCancel = () => {
    if (isDirty) {
      setConfirmDialogOpen(true);
    }
  };

  const handleConfirmLeave = () => {
    setConfirmDialogOpen(false);
    navigate('/blog');
  };

  if (loading) {
    return <LoadingState message="Loading post..." minHeight={400} />;
  }

  if (loadError) {
    return (
      <Alert variant="destructive" className="mb-4">
        <AlertDescription className="flex items-center justify-between gap-4">
          <span>{loadError}</span>
          <Button variant="outline" size="sm" onClick={fetchData}>
            Retry
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleBack}
            title="Back to Blog List"
          >
            <ArrowLeft />
          </Button>
          <h1 className="truncate text-token-xl font-semibold">
            {isNew ? 'Create New Post' : `Edit: ${originalData?.title || 'Post'}`}
          </h1>
        </div>

        {!readOnly && (
          <div className="flex items-center gap-2">
            {isDirty && (
              <Button variant="ghost" size="sm" onClick={handleCancel}>
                <CancelIcon /> Cancel
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleSave('draft')}
              disabled={saving}
            >
              {savingAction === 'draft' || savingAction === 'save' ? (
                <Loader2 className="animate-spin" />
              ) : (
                <Save />
              )}
              Save draft
            </Button>
            <Button
              size="sm"
              onClick={() => handleSave('pending_review')}
              disabled={saving}
            >
              {savingAction === 'pending_review' ? (
                <Loader2 className="animate-spin" />
              ) : (
                <Send />
              )}
              Submit for review
            </Button>
          </div>
        )}
      </div>

      {/* Status notices */}
      {readOnly && (
        <Alert className="mb-4">
          <AlertDescription className="flex items-center gap-2">
            <Lock className="h-4 w-4 shrink-0" />
            <span>
              This post is {formData.status === 'published' ? 'published' : 'archived'}{' '}
              and is managed by DraftFC. It can no longer be edited here.
            </span>
          </AlertDescription>
        </Alert>
      )}
      {!readOnly && formData.status === 'pending_review' && (
        <Alert className="mb-4">
          <AlertDescription>
            This post is awaiting review by DraftFC. You can still make changes
            and re-submit, or save it back to draft to withdraw it from review.
          </AlertDescription>
        </Alert>
      )}

      {/* Form */}
      <div className="flex flex-col gap-5 rounded-token-large border border-borderGrey bg-white p-6">
        {/* Title */}
        <div className="grid gap-1.5">
          <Label htmlFor="post-title">
            Title <span className="text-destructive">*</span>
          </Label>
          <Input
            id="post-title"
            value={formData.title}
            onChange={(e) => setField('title', e.target.value)}
            disabled={readOnly}
            required
          />
        </div>

        {/* Featured Image Upload */}
        <div className="grid gap-1.5">
          <Label>Featured Image</Label>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            hidden
            onChange={handleImageUpload}
          />
          {formData.featured_image_url ? (
            <div className="flex items-start gap-4">
              <img
                src={formData.featured_image_url}
                alt="Featured"
                className="max-h-[160px] max-w-[240px] rounded-token-medium border border-borderGrey object-cover"
              />
              {!readOnly && (
                <Button
                  variant="outline"
                  size="sm"
                  className="border-destructive/60 text-destructive hover:bg-destructive/5"
                  onClick={handleRemoveImage}
                >
                  <Trash2 /> Remove
                </Button>
              )}
            </div>
          ) : (
            <div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading || readOnly}
              >
                {uploading ? <Loader2 className="animate-spin" /> : <Upload />}
                Upload Image
              </Button>
            </div>
          )}
        </div>

        {/* Excerpt */}
        <div className="grid gap-1.5">
          <Label htmlFor="post-excerpt">Excerpt</Label>
          <Textarea
            id="post-excerpt"
            rows={2}
            maxLength={500}
            value={formData.excerpt}
            onChange={(e) => setField('excerpt', e.target.value)}
            disabled={readOnly}
          />
          <p className="text-token-base text-muted-foreground">
            {formData.excerpt.length}/500 characters
          </p>
        </div>

        {/* Content Editor */}
        <div className="grid gap-1.5">
          <Label>Content</Label>
          <TipTapEditor
            content={formData.content}
            onChange={handleContentChange}
            minHeight={350}
          />
        </div>

        {/* Category, Season, Gameweek */}
        <div className="flex flex-wrap gap-4">
          <div className="grid min-w-[160px] gap-1.5">
            <Label>Category</Label>
            <Select
              value={formData.category_id ? String(formData.category_id) : 'none'}
              onValueChange={(v) =>
                setField('category_id', v === 'none' ? '' : parseInt(v, 10))
              }
              disabled={readOnly}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">
                  <span className="italic text-muted-foreground">None</span>
                </SelectItem>
                {categories.filter((c) => c.is_active !== false).map((cat) => (
                  <SelectItem key={cat.id} value={String(cat.id)}>
                    {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid min-w-[140px] gap-1.5">
            <Label>Season</Label>
            <Select
              value={formData.season || 'none'}
              onValueChange={(v) => setField('season', v === 'none' ? '' : v)}
              disabled={readOnly}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">
                  <span className="italic text-muted-foreground">None</span>
                </SelectItem>
                {seasonOptions.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid min-w-[170px] gap-1.5">
            <Label>Gameweek</Label>
            <Select
              value={formData.gameweek === '' || formData.gameweek === null
                ? 'none'
                : String(formData.gameweek)}
              onValueChange={(v) =>
                setField('gameweek', v === 'none' ? '' : parseInt(v, 10))
              }
              disabled={readOnly}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">
                  <span className="italic text-muted-foreground">General / Preseason</span>
                </SelectItem>
                {[...Array(38)].map((_, i) => (
                  <SelectItem key={i + 1} value={String(i + 1)}>
                    GW {i + 1}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* SEO Settings */}
        <p className="mt-1 text-token-md font-medium text-muted-foreground">
          SEO Settings (Optional)
        </p>

        <div className="grid gap-1.5">
          <Label htmlFor="post-meta-title">Meta Title</Label>
          <Input
            id="post-meta-title"
            value={formData.meta_title}
            onChange={(e) => setField('meta_title', e.target.value)}
            disabled={readOnly}
          />
          <p className="text-token-base text-muted-foreground">
            Override the page title for search engines
          </p>
        </div>

        <div className="grid gap-1.5">
          <Label htmlFor="post-meta-description">Meta Description</Label>
          <Textarea
            id="post-meta-description"
            rows={2}
            maxLength={500}
            value={formData.meta_description}
            onChange={(e) => setField('meta_description', e.target.value)}
            disabled={readOnly}
          />
          <p className="text-token-base text-muted-foreground">
            {formData.meta_description.length}/500 characters
            {formData.meta_description.length > 160 &&
              ' — over ~160 will be truncated in search results'}
          </p>

          {/* Search snippet preview */}
          <div className="mt-1 max-w-[600px] rounded-token-medium border border-borderGrey bg-white p-3">
            <p className="truncate text-[18px] leading-snug text-[#1a0dab]">
              {formData.meta_title || formData.title || 'Page title'}
            </p>
            <p className="text-token-base text-[#006621]">draftfc.co.uk › blog</p>
            <p className="line-clamp-2 text-token-base text-textSecondary">
              {formData.meta_description ||
                formData.excerpt ||
                'Meta description preview will appear here.'}
            </p>
          </div>
        </div>
      </div>

      {/* Discard changes confirmation */}
      <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle>Discard Changes?</DialogTitle>
          </DialogHeader>
          <p className="text-token-md">
            You have unsaved changes. Are you sure you want to leave? Your changes
            will be lost.
          </p>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setConfirmDialogOpen(false)}>
              Keep Editing
            </Button>
            <Button variant="destructive" size="sm" onClick={handleConfirmLeave}>
              Discard Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
